import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const PRACTICE_AREAS = [
  "Personal Injury", "Auto Accidents", "Employment", "Wrongful Termination",
  "Family Law", "Divorce", "Child Custody", "Immigration", "Criminal Defense",
  "Contracts", "Construction", "Business Litigation", "Probate",
  "Estate Matters", "Landlord-Tenant", "Real Estate", "Workers Compensation",
  "Disability Claims", "Medical Malpractice", "Consumer Protection",
  "Civil Litigation",
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    role: "attorney",
    name: "",
    firm_name: "",
    email: "",
    password: "",
    phone: "",
    website: "",
    bar_number: "",
    states_licensed: [],
    practice_areas: [],
  });

  const toggle = (key, value) =>
    setForm((f) => {
      const arr = f[key];
      return {
        ...f,
        [key]: arr.includes(value)
          ? arr.filter((v) => v !== value)
          : [...arr, value],
      };
    });

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.states_licensed.length === 0 || form.practice_areas.length === 0) {
      toast.error("Select at least one state and one practice area");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      navigate("/pending", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="py-12 lg:py-20 bg-[#F8FAFC]">
      <div className="max-w-2xl mx-auto px-6">
        <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#C5A059] mb-3">
          For Attorneys & Law Firms
        </p>
        <h1 className="font-heading text-4xl lg:text-5xl font-bold text-[#0A192F] tracking-tight">
          Apply for approval
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          Tell us about your practice. An admin will review your credentials,
          then you can subscribe to access matched cases.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-8 bg-white border border-slate-200 rounded-xl p-6 space-y-6"
        >
          <div>
            <Label className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
              Account Type
            </Label>
            <Select value={form.role} onValueChange={update("role")}>
              <SelectTrigger
                data-testid="register-role"
                className="mt-2 rounded-lg border-slate-300"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="attorney" data-testid="role-attorney">Individual Attorney</SelectItem>
                <SelectItem value="law_firm" data-testid="role-law-firm">Law Firm</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormInput
              label="Full Name"
              testId="register-name"
              value={form.name}
              onChange={update("name")}
              required
              placeholder="Jane Smith"
            />
            <FormInput
              label="Firm Name"
              testId="register-firm"
              value={form.firm_name}
              onChange={update("firm_name")}
              placeholder="Smith & Co. Law"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormInput
              label="Email"
              type="email"
              testId="register-email"
              value={form.email}
              onChange={update("email")}
              required
              placeholder="jane@firm.com"
            />
            <FormInput
              label="Password"
              type="password"
              testId="register-password"
              value={form.password}
              onChange={update("password")}
              required
              placeholder="At least 8 characters"
            />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <FormInput
              label="Phone"
              testId="register-phone"
              value={form.phone}
              onChange={update("phone")}
              placeholder="(555) 555-5555"
            />
            <FormInput
              label="Website"
              testId="register-website"
              value={form.website}
              onChange={update("website")}
              placeholder="https://"
            />
            <FormInput
              label="Bar Number"
              testId="register-bar"
              value={form.bar_number}
              onChange={update("bar_number")}
              placeholder="CA #123456"
            />
          </div>

          <div>
            <Label className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
              States Licensed
            </Label>
            <div
              data-testid="register-states"
              className="mt-2 flex flex-wrap gap-2"
            >
              {US_STATES.map((s) => {
                const active = form.states_licensed.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`state-pill-${s}`}
                    onClick={() => toggle("states_licensed", s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      active
                        ? "bg-[#0A192F] border-[#0A192F] text-white"
                        : "bg-white border-slate-200 text-slate-700 hover:border-[#C5A059]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
              Practice Areas
            </Label>
            <div
              data-testid="register-practice-areas"
              className="mt-2 flex flex-wrap gap-2"
            >
              {PRACTICE_AREAS.map((p) => {
                const active = form.practice_areas.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    data-testid={`practice-pill-${p.replace(/\W+/g, "-").toLowerCase()}`}
                    onClick={() => toggle("practice_areas", p)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                      active
                        ? "bg-[#C5A059] border-[#C5A059] text-[#0A192F]"
                        : "bg-white border-slate-200 text-slate-700 hover:border-[#C5A059]"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            type="submit"
            data-testid="register-submit-btn"
            disabled={loading}
            className="w-full bg-[#0A192F] hover:bg-[#112A46] text-white rounded-lg py-5"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
          <p className="text-xs text-slate-500 text-center">
            By registering you confirm you are an attorney and your bar
            credentials are accurate. Admin review takes 1-2 business days.
          </p>
        </form>

        <p className="mt-6 text-sm text-center text-slate-600">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0A192F] underline font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function FormInput({ label, testId, value, onChange, required, placeholder, type = "text" }) {
  return (
    <div>
      <Label className="text-xs tracking-[0.18em] uppercase font-semibold text-slate-500">
        {label}
      </Label>
      <Input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="mt-2 rounded-lg border-slate-300"
      />
    </div>
  );
}
